import { currentLocale, intlLocaleFor } from "../i18n";

/**
 * Timestamps arrive as ISO-8601 UTC (`timestamptz` on the wire) and are shown
 * in the VIEWER'S OWN time zone — the browser's. The platform serves admins
 * across SEA (MYT, ICT, WIB, PHT…); a person in Bangkok reads Bangkok time,
 * the stored instant is the same for everyone. `formatDateTime` spells the
 * zone out, so a screenshot is never ambiguous.
 *
 * No relative times ("just now", "3 hours ago") anywhere: a help desk
 * comparing two events needs the clock, not a feeling.
 *
 * Dates are DD/MM/YYYY everywhere in the GMS, in every language (user,
 * 2026-10-31: one standard for the 11 countries) — `formatYmd` is the one
 * shape. Only weekday names come from Intl in the active UI locale —
 * resolved per call, so a language switch reformats on the next render.
 */

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** "15/11/2026" — a calendar date (`YYYY-MM-DD…`, already in its zone: no zone math). "—" when unreadable. */
export function formatYmd(ymd: string | null | undefined): string {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd ?? "");
	return m ? `${m[3]}/${m[2]}/${m[1]}` : "—";
}

/** "15/11" — day and month only, where a year would not fit (chart ticks); the full date goes in the tooltip. */
export function formatYmdShort(ymd: string | null | undefined): string {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd ?? "");
	return m ? `${m[3]}/${m[2]}` : "—";
}

/** An instant's DD/MM/YYYY in the viewer's own zone. */
const viewerDmy = (d: Date): string => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;

/**
 * "Sun 23/08/2026 | 4:07PM" — the USER PAGE stamp (profile, activity feed,
 * sign-ins, sessions). It is deliberately loud; in dense tables use
 * `formatShort`.
 */
export function formatStamp(iso: string | null | undefined): string {
	const d = toDate(iso);
	if (!d) return "—";
	return `${viewerFormatters().weekday.format(d)} ${viewerDmy(d)} | ${formatClock(d)}`;
}

/** "23/08/2026, 3:49PM" — compact, for table cells. */
export function formatShort(iso: string | null | undefined): string {
	const d = toDate(iso);
	if (!d) return "—";
	return `${viewerDmy(d)}, ${formatClock(d)}`;
}

/**
 * "4:07PM" — no space, no leading zero. Deliberately hand-rolled and NOT
 * localized: whether the clock follows the locale (24h, localized day
 * periods) is a product decision still pending, so it stays as-is for now.
 */
export function formatClock(d: Date): string {
	const h24 = d.getHours();
	const h = h24 % 12 === 0 ? 12 : h24 % 12;
	const m = String(d.getMinutes()).padStart(2, "0");
	return `${h}:${m}${h24 < 12 ? "AM" : "PM"}`;
}

/** Long form for tooltips: "Sun 23/08/2026, 4:07PM (GMT+8)". */
export function formatDateTime(iso: string | null | undefined): string {
	const d = toDate(iso);
	if (!d) return "—";
	return `${viewerFormatters().weekday.format(d)} ${viewerDmy(d)}, ${formatClock(d)} (${zoneLabel(d)})`;
}

/** "23/08/2026" in the viewer's zone. */
export function formatDate(iso: string | null | undefined): string {
	const d = toDate(iso);
	return d ? viewerDmy(d) : "—";
}

/** "23/08/2026" — an instant's date in `timeZone` (the games' own, like `formatClockIn`). */
export function formatDateIn(iso: string | null | undefined, timeZone: string): string {
	const w = wallTimeIn(iso, timeZone);
	return w ? formatYmd(w.date) : "—";
}

/** The viewer's IANA zone, e.g. "Asia/Bangkok" — for a footer or a settings screen. */
export const viewerTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

// ── A fixed zone: the games' own (M9 travel) ────────────────────────────────
//
// Travel is the one place a time is shown in a zone that is NOT the viewer's:
// a flight lands at 1:00PM Malaysia time whoever reads it, and the A&D desk,
// the drivers and the hotels all speak that clock (PLAN M9 T6). Those screens
// label the time with the games' zone ("1:00PM MYT") and put the viewer's own
// reading in the tooltip (`formatDateTime`), so the rule above still holds.

/**
 * Zones people know by a name Intl does not print: Intl calls
 * Asia/Kuala_Lumpur "GMT+8", every Malaysian (and the designer's prototype)
 * says MYT. Anything else falls back to Intl's short name.
 */
const ZONE_ABBREVIATIONS: Readonly<Record<string, string>> = { "Asia/Kuala_Lumpur": "MYT" };
const DAY_SHORT = new Map<string, Intl.DateTimeFormat>();

/** "MYT" for the games' zone; Intl's short name ("GMT+7") for any other. */
export function zoneAbbreviation(timeZone: string, at: Date = new Date()): string {
	const known = ZONE_ABBREVIATIONS[timeZone];
	if (known) return known;
	try {
		return new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" }).formatToParts(at).find((p) => p.type === "timeZoneName")?.value ?? timeZone;
	} catch {
		return timeZone;
	}
}

/** The wall clock of an instant in `timeZone`: `{ date: "2026-11-03", time: "13:00" }`. Null when unreadable. */
export function wallTimeIn(iso: string | null | undefined, timeZone: string): { date: string; time: string } | null {
	const d = toDate(iso);
	if (!d) return null;
	const w = wallParts(d, timeZone);
	if (!w) return null;
	const p = (n: number) => String(n).padStart(2, "0");
	return { date: `${w.year}-${p(w.month)}-${p(w.day)}`, time: `${p(w.hour)}:${p(w.minute)}` };
}

/**
 * `"2026-11-03"` + `"13:00"` read as a wall clock in `timeZone` → the instant,
 * ISO UTC. Null for anything that is not a real date and time. A wall time
 * a DST jump skips resolves to the instant after the jump (no zone this
 * platform serves has DST; the loop is there so a future one is still right).
 */
export function wallTimeToIso(date: string, time: string, timeZone: string): string | null {
	const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
	const tm = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(time);
	if (!dm || !tm) return null;
	const [y, mo, da, h, mi] = [Number(dm[1]), Number(dm[2]), Number(dm[3]), Number(tm[1]), Number(tm[2])];
	if (h > 23 || mi > 59) return null;
	const asUtc = Date.UTC(y, mo - 1, da, h, mi);
	const check = new Date(asUtc);
	if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== da) return null;
	let guess = asUtc;
	for (let i = 0; i < 2; i++) {
		const offset = zoneOffsetMs(new Date(guess), timeZone);
		if (offset === null) return null;
		guess = asUtc - offset;
	}
	return new Date(guess).toISOString();
}

/** "1:00PM" — `formatClock`'s shape, read in `timeZone` instead of the viewer's zone. */
export function formatClockIn(iso: string | null | undefined, timeZone: string): string {
	const d = toDate(iso);
	const w = d ? wallParts(d, timeZone) : null;
	if (!w) return "—";
	const h = w.hour % 12 === 0 ? 12 : w.hour % 12;
	return `${h}:${String(w.minute).padStart(2, "0")}${w.hour < 12 ? "AM" : "PM"}`;
}

/**
 * "Mon 03/11/2026" — a calendar date (`YYYY-MM-DD`, already in the zone it
 * belongs to: no zone math): the weekday in the UI locale, then DD/MM/YYYY.
 */
export function formatDayShort(ymd: string | null | undefined): string {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd ?? "");
	if (!m) return "—";
	return `${formatWeekday(ymd)} ${formatYmd(ymd)}`;
}

const MONTH_YEAR = new Map<string, Intl.DateTimeFormat>();

/** "November 2026" — a calendar month's heading (a month view's title, never a date: dates stay DD/MM/YYYY). */
export function formatMonthYear(ymd: string | null | undefined): string {
	const m = /^(\d{4})-(\d{2})/.exec(ymd ?? "");
	if (!m) return "—";
	const loc = intlLocaleFor(currentLocale());
	let f = MONTH_YEAR.get(loc);
	if (!f) {
		f = new Intl.DateTimeFormat(loc === "en" ? "en-GB" : loc, { month: "long", year: "numeric", timeZone: "UTC" });
		MONTH_YEAR.set(loc, f);
	}
	return f.format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)));
}

/** "Mon" — a calendar date's weekday in the UI locale. */
export function formatWeekday(ymd: string | null | undefined): string {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd ?? "");
	if (!m) return "—";
	const loc = intlLocaleFor(currentLocale());
	let f = DAY_SHORT.get(loc);
	if (!f) {
		f = new Intl.DateTimeFormat(loc === "en" ? "en-GB" : loc, { weekday: "short", timeZone: "UTC" });
		DAY_SHORT.set(loc, f);
	}
	return f.format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))));
}

interface WallParts {
	year: number;
	month: number;
	day: number;
	hour: number;
	minute: number;
	second: number;
}

/** One `formatToParts` formatter per zone; `en-US` + `h23` so the numbers parse the same everywhere. */
const ZONE_PARTS = new Map<string, Intl.DateTimeFormat | null>();

function wallParts(d: Date, timeZone: string): WallParts | null {
	let f = ZONE_PARTS.get(timeZone);
	if (f === undefined) {
		try {
			f = new Intl.DateTimeFormat("en-US", {
				timeZone,
				hourCycle: "h23",
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
			});
		} catch {
			f = null; // an unknown IANA name
		}
		ZONE_PARTS.set(timeZone, f);
	}
	if (!f) return null;
	const parts = f.formatToParts(d);
	const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
	return { year: get("year"), month: get("month"), day: get("day"), hour: get("hour") % 24, minute: get("minute"), second: get("second") };
}

/** Wall clock minus UTC, in ms, at that instant (MYT: +8 h). */
function zoneOffsetMs(d: Date, timeZone: string): number | null {
	const w = wallParts(d, timeZone);
	if (!w) return null;
	return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - (d.getTime() - d.getUTCMilliseconds());
}

// ── internals ───────────────────────────────────────────────────────────────

interface Formatters {
	weekday: Intl.DateTimeFormat;
	zone: Intl.DateTimeFormat;
}

/** Intl.DateTimeFormat construction is expensive; memoize per Intl locale. */
const FORMATTERS = new Map<string, Formatters>();

function formattersFor(intlLocale: string): Formatters {
	let f = FORMATTERS.get(intlLocale);
	if (!f) {
		f = {
			weekday: new Intl.DateTimeFormat(intlLocale, { weekday: "short" }),
			zone: new Intl.DateTimeFormat(intlLocale, { timeZoneName: "short" }),
		};
		FORMATTERS.set(intlLocale, f);
	}
	return f;
}

/** Resolved per call — never at module load — so a language switch takes effect. */
function viewerFormatters(): Formatters {
	return formattersFor(intlLocaleFor(currentLocale()));
}

function toDate(iso: string | null | undefined): Date | null {
	if (!iso) return null;
	const d = new Date(iso);
	return Number.isNaN(d.getTime()) ? null : d;
}

function zoneLabel(d: Date): string {
	return viewerFormatters().zone.formatToParts(d).find((p) => p.type === "timeZoneName")?.value ?? viewerTimeZone();
}
